import * as React from "react";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { makeStyles, createStyles, Theme, IconButton } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";

const DEFAULT_DISPLAY_MS = 5000;
const DEFAULT_TRANSITION_MS = 600;
const PROGRESS_TICK_MS = 50;

type StrapiImage = {
  localFile?: {
    childImageSharp?: { gatsbyImageData: IGatsbyImageData };
    publicURL?: string;
  };
};

export type HeroCarouselSlideData = {
  image?: StrapiImage | null;
  alt?: string | null;
  displayDurationMs?: number | null;
};

export type HeroCarouselData = {
  autoplay?: boolean | null;
  transitionDurationMs?: number | null;
  slides?: HeroCarouselSlideData[] | null;
};

const useStyles = makeStyles((t: Theme) =>
  createStyles({
    root: {
      width: "100%",
    },
    carousel: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      [t.breakpoints.up("md")]: {
        gap: 24,
      },
    },
    navButton: {
      flexShrink: 0,
      width: 48,
      height: 48,
      border: `1px solid ${t.palette.primary.main}`,
      color: t.palette.primary.main,
      backgroundColor: "#fff",
      transition: "background-color 0.2s, color 0.2s",
      "&:hover": {
        backgroundColor: t.palette.primary.main,
        color: "#fff",
      },
      [t.breakpoints.down("xs")]: {
        width: 40,
        height: 40,
      },
    },
    navHidden: {
      visibility: "hidden",
      pointerEvents: "none",
    },
    stageWrap: {
      flex: 1,
      minWidth: 0,
      maxWidth: 900,
    },
    stage: {
      position: "relative",
      width: "100%",
      textAlign: "center",
    },
    stageSizer: {
      visibility: "hidden",
      pointerEvents: "none",
      "& img, & .gatsby-image-wrapper": {
        width: "100%",
        height: "auto",
        display: "block",
        margin: "0 auto",
      },
    },
    slide: {
      width: "100%",
      transitionProperty: "opacity",
      transitionTimingFunction: "ease-in-out",
      "& img, & .gatsby-image-wrapper": {
        width: "100%",
        height: "auto",
        display: "block",
        margin: "0 auto",
      },
    },
    slideLayer: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
    },
    dots: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: 8,
      marginTop: 24,
      minHeight: 12,
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      padding: 0,
      border: "none",
      backgroundColor: "rgba(234, 22, 116, 0.25)",
      cursor: "pointer",
      transition: "background-color 0.2s, width 0.2s, border-radius 0.2s",
      overflow: "hidden",
      position: "relative",
    },
    dotActive: {
      backgroundColor: t.palette.primary.main,
    },
    dotProgressTrack: {
      width: 32,
      height: 8,
      borderRadius: 4,
      backgroundColor: "rgba(234, 22, 116, 0.25)",
      padding: 0,
      border: "none",
      cursor: "pointer",
      overflow: "hidden",
      position: "relative",
    },
    dotProgressFill: {
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      backgroundColor: t.palette.primary.main,
      borderRadius: 4,
    },
  })
);

const SlideMedia: React.FC<{
  image: StrapiImage;
  alt: string;
  className?: string;
}> = ({ image, alt, className }) => {
  const gatsbyImage = image.localFile?.childImageSharp?.gatsbyImageData;
  const url = image.localFile?.publicURL;

  if (gatsbyImage) {
    return <GatsbyImage image={gatsbyImage} alt={alt} className={className} />;
  }
  if (url) {
    return <img src={url} alt={alt} className={className} />;
  }
  return null;
};

const parseDisplayDurationMs = (value?: number | string | null) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1000) {
    return DEFAULT_DISPLAY_MS;
  }
  return parsed;
};

const parseTransitionMs = (value?: number | string | null) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_TRANSITION_MS;
  }
  return Math.min(3000, Math.max(100, parsed));
};

const HeroImageCarousel: React.FC<{ data: HeroCarouselData }> = ({ data }) => {
  const classes = useStyles();
  const slides = useMemo(
    () =>
      data.slides?.filter(
        (s) => s?.image?.localFile?.childImageSharp || s?.image?.localFile?.publicURL
      ) ?? [],
    [data.slides]
  );

  const count = slides.length;
  const autoplay = data.autoplay !== false && count > 1;
  const transitionMs = parseTransitionMs(data.transitionDurationMs);

  const [index, setIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  const goTo = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
      setProgress(0);
    },
    [count]
  );

  const activeDisplayDurationMs = parseDisplayDurationMs(slides[index]?.displayDurationMs);

  useEffect(() => {
    if (!autoplay || count <= 1) {
      setProgress(0);
      return undefined;
    }

    const duration = parseDisplayDurationMs(slidesRef.current[index]?.displayDurationMs);
    const start = Date.now();
    let done = false;

    const id = window.setInterval(() => {
      if (done) return;
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / duration) * 100);
      setProgress(pct);
      if (pct >= 100) {
        done = true;
        window.clearInterval(id);
        goTo((index + 1) % count);
      }
    }, PROGRESS_TICK_MS);

    return () => {
      done = true;
      window.clearInterval(id);
    };
  }, [index, autoplay, count, activeDisplayDurationMs, goTo]);

  if (count === 0) {
    return null;
  }

  const showNav = count > 1;
  const multiSlide = count > 1;

  return (
    <div className={classes.root}>
      <div className={classes.carousel}>
        <IconButton
          className={[classes.navButton, !showNav && classes.navHidden].filter(Boolean).join(" ")}
          onClick={() => goTo(index - 1)}
          aria-label="Previous image"
          disableRipple
        >
          <ChevronLeftIcon />
        </IconButton>

        <div className={classes.stageWrap}>
          <div className={classes.stage}>
            {multiSlide && (
              <div className={classes.stageSizer} aria-hidden>
                <SlideMedia
                  image={slides[index]?.image ?? slides[0].image!}
                  alt=""
                />
              </div>
            )}
            {slides.map((slide, i) => {
              const isActive = i === index;
              const alt = slide.alt?.trim() || `Slide ${i + 1}`;
              return (
                <div
                  key={i}
                  className={[classes.slide, multiSlide && classes.slideLayer]
                    .filter(Boolean)
                    .join(" ")}
                  style={{
                    opacity: multiSlide ? (isActive ? 1 : 0) : 1,
                    zIndex: isActive ? 1 : 0,
                    transitionDuration: `${transitionMs}ms`,
                  }}
                  aria-hidden={!isActive}
                >
                  <SlideMedia image={slide.image!} alt={alt} />
                </div>
              );
            })}
          </div>
        </div>

        <IconButton
          className={[classes.navButton, !showNav && classes.navHidden].filter(Boolean).join(" ")}
          onClick={() => goTo(index + 1)}
          aria-label="Next image"
          disableRipple
        >
          <ChevronRightIcon />
        </IconButton>
      </div>

      {showNav && (
        <div className={classes.dots} role="tablist" aria-label="Hero images">
          {slides.map((_, i) => {
            const isActive = i === index;
            if (isActive && autoplay) {
              return (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected
                  aria-label={`Image ${i + 1}, ${Math.round(progress)}%`}
                  className={classes.dotProgressTrack}
                  onClick={() => goTo(i)}
                >
                  <span
                    key={`progress-${index}`}
                    className={classes.dotProgressFill}
                    style={{ width: `${progress}%` }}
                  />
                </button>
              );
            }
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-label={`Image ${i + 1}`}
                className={[classes.dot, isActive && classes.dotActive].filter(Boolean).join(" ")}
                onClick={() => goTo(i)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HeroImageCarousel;
